// cascade-columns.ts — canonical price column structure of the entry table (book 4.6 §5, block C).
//
// Single source of truth for the per-line price cascade column order: both the table header and body
// iterate this list, and the "UI CASCADE" engine-test assertion locks the order against regressions.
// Changing the order here breaks that test: a conscious decision.
//
// Order: public PV → agency discount % → agency net PV → tier discount % → tier net PV → line discount %
// (editable) → PV €/m² (editable) → theoretical unit price → minimum unit price (forceable) → final unit
// price (locked) → total → [three PA groups side by side] TARGET → RATE TABLE → NEGOTIATED (each €/m² ·
// €/u · margin %) → [UPZ group] gross material PA → waste % → net material PA.
// (Between "total" and the first PA group, the UI inserts the chosen workshop and the material supply —
// outside this list.)
//
// The two editable cells (lineDiscountAmt, pvm2) are two views of one override synced on the public sale
// price (entering either recomputes the other); they break the cascade (agency/tier stay at the engine
// forecast) and turn orange. Under a net-price rate table both become read-only.

/** Role of a cascade column (drives rendering and legends). */
export type CascadeRole =
  | "engine" // frozen engine forecast (never editable)
  | "override" // editable, override synced on public sale price (orange + reset)
  | "forceable" // editable, floor forcing (minimum unit price)
  | "computed" // locked, computed (final unit price, total)
  | "margin" // margin group — hidden without the see-margin right
  | "pa"; // purchase price column (read-only in phase 2)

/** Price column descriptor: stable key (data-col + render switch) + i18n key of the header label + role. */
export interface CascadeColumn {
  key: string;
  labelKey: string;
  role: CascadeRole;
}

/** Cascade price columns, in the canonical block C order (locked by test). */
export const CASCADE_COLUMNS: readonly CascadeColumn[] = [
  { key: "pvPublic", labelKey: "engineCascadePvPublic", role: "engine" },
  { key: "agencyDisc", labelKey: "engineCascadeAgencyDisc", role: "engine" },
  { key: "pvNetAg", labelKey: "engineCascadePvNetAg", role: "engine" },
  { key: "tierDiscount", labelKey: "engineCascadeTierDiscount", role: "engine" },
  { key: "tierNetPv", labelKey: "engineCascadeTierNetPv", role: "engine" },
  { key: "lineDiscountAmt", labelKey: "engineCascadeLineDiscountAmt", role: "override" },
  { key: "pvm2", labelKey: "engineCascadePvm2", role: "override" },
  { key: "theoreticalUnitPrice", labelKey: "engineCascadeTheoreticalUnitPrice", role: "engine" },
  { key: "minUnitPrice", labelKey: "engineCascadeMinUnitPrice", role: "forceable" },
  { key: "defaultUnitPrice", labelKey: "engineCascadeDefaultUnitPrice", role: "computed" },
  { key: "total", labelKey: "engineCascadeTotal", role: "computed" },
  // Three side-by-side purchase price groups (target / rate table / negotiated), each: PA €/m² · PA €/u · margin %.
  // Hidden as a block without the margin right. Negotiated €/m² and €/u are editable (they write the manual
  // PA); the rest is read-only.
  // Target purchase price (permanent default, tier reference)
  { key: "targetPaM2", labelKey: "engineCascadeTargetPaM2", role: "pa" },
  { key: "targetPaU", labelKey: "engineCascadeTargetPaU", role: "pa" },
  { key: "marginTargetPct", labelKey: "engineCascadeMarginTargetPct", role: "margin" },
  // Rate-table purchase price (real workshop cost, when nothing is negotiated)
  { key: "gridPaM2", labelKey: "engineCascadeGridPaM2", role: "pa" },
  { key: "gridPaU", labelKey: "engineCascadeGridPaU", role: "pa" },
  { key: "gridMarginPct", labelKey: "engineCascadeGridMarginPct", role: "margin" },
  // Negotiated purchase price (entered by hand, wins over rate table and target)
  { key: "negotiatedPaM2", labelKey: "engineCascadeNegotiatedPaM2", role: "pa" },
  { key: "negotiatedPaU", labelKey: "engineCascadeNegotiatedPaU", role: "pa" },
  { key: "negotiatedPaTotal", labelKey: "engineCascadeNegotiatedPaTotal", role: "pa" },
  { key: "negotiatedMarginPct", labelKey: "engineCascadeNegotiatedMarginPct", role: "margin" },
  // In-house stock material purchase price group (book 4.6 §18): gross, waste coefficient, then net (net feeds the margin).
  { key: "paMatGross", labelKey: "engineCascadePaMatGross", role: "pa" },
  { key: "paMatCoeff", labelKey: "engineCascadePaMatCoeff", role: "pa" },
  { key: "paMatNet", labelKey: "engineCascadePaMatNet", role: "pa" },
] as const;

/** Canonical key order (for the non-regression assertion and DOM tests). */
export const CASCADE_ORDER: readonly string[] = CASCADE_COLUMNS.map((c) => c.key);
